/**
 * @description Custom hook for Elavon Hosted Payment Fields (HPF) SDK lifecycle.
 * Manages session creation, HPF script loading, field initialization, validation
 * state tracking, submission, cleanup, and auto-recovery after failures.
 *
 * Key features:
 * - One-time initialization on mount (no re-creation on parent re-renders)
 * - HPF script loading with deduplication (safe for multiple concurrent mounts)
 * - Field validation state tracking via messageHandler events
 * - Submit accepts billTo data from the parent component's billing fields
 * - Automatic cleanup via destroy() on unmount
 * - Accepts pre-fetched clientConfig to avoid redundant getClientConfig() call
 * - Auto-recovery: on SDK errors (e.g. session consumed), destroys old instance
 *   and reinitializes with a fresh session so the user can retry without refreshing
 *
 * @dependencies
 * - paymentSessionService: getClientConfig(), createPaymentSession()
 * - ElavonHostedFields: Global from HPF script (window.ElavonHostedFields)
 *
 * @notes
 * - sessionId is created ONCE on mount — amount/currency/description are captured
 *   at initialization time and do NOT trigger re-initialization if they change.
 *   This is intentional: EPG sessions are immutable once created.
 * - Callbacks (onCardReady, onError) are stored in refs to avoid stale closures
 *   without causing re-initialization.
 * - HPF sessions expire after 30 minutes (ElavonConfig.SESSION_TTL_MINUTES).
 * - Field wrapperId values must match the DOM element IDs rendered by the
 *   consuming component (HostedPaymentFields.js).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { paymentSessionService } from '../services/api';

// Field config — wrapperId must match DOM element IDs in HostedPaymentFields.js.
// Placeholders are per-field per Elavon SDK docs (not a top-level option).
const FIELD_CONFIG = {
  cardNumber: {
    wrapperId: 'hpf-card-number',
    placeholder: '1234 5678 9012 3456'
  },
  cardExpirationDate: {
    wrapperId: 'hpf-expiry',
    placeholder: 'MM / YY'
  },
  cardCvv: {
    wrapperId: 'hpf-cvv',
    placeholder: 'CVV'
  },
  cardholderName: {
    wrapperId: 'hpf-cardholder-name',
    placeholder: 'John Doe'
  }
};

// Styles injected into HPF iFrames — controls the input INSIDE each iframe.
// Height/padding must match PaymentCardForm's Tailwind `px-3 py-2` (38px total).
const HPF_STYLES = {
  'input': {
    'font-family': 'ui-sans-serif, system-ui, sans-serif',
    'font-size': '14px',
    'color': '#111827',
    'padding': '8px 12px',
    'width': '100%',
    'height': '100%',
    'box-sizing': 'border-box',
    'border': 'none',
    'background': 'transparent'
  },
  'input:focus': {
    'outline': 'none'
  },
  'input::placeholder': {
    'color': '#9ca3af'
  }
};

/**
 * Script loading state — shared across all hook instances.
 * Prevents duplicate <script> tags if multiple components mount simultaneously.
 */
let scriptLoadPromise = null;

/**
 * Load the HPF JS SDK script once, with deduplication.
 * @param {string} scriptUrl - Full URL to the HPF client script
 * @returns {Promise<void>}
 */
function loadHpfScript(scriptUrl) {
  // Already loaded
  if (window.ElavonHostedFields) {
    return Promise.resolve();
  }

  // Another instance already started loading — piggyback on its promise
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null; // Allow retry on failure
      reject(new Error('Failed to load HPF script'));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

// Initial state constants — used on mount and during recovery resets
const INITIAL_FIELD_VALIDITY = {
  cardNumber: null,
  cardExpirationDate: null,
  cardCvv: null,
  cardholderName: null
};
const INITIAL_FIELDS_LOADED = {
  cardNumber: false,
  cardExpirationDate: false,
  cardCvv: false,
  cardholderName: false
};

/**
 * @param {object} options
 * @param {number} options.amount - Amount in cents (captured at init, not reactive)
 * @param {string} [options.currency='USD'] - ISO 4217 currency code
 * @param {string} [options.description=''] - Payment description
 * @param {object} [options.clientConfig=null] - Pre-fetched client config from CardEntry (skips redundant API call)
 * @param {function} options.onCardReady - Called with hostedCard data after successful HPF submit
 * @param {function} [options.onError] - Called on SDK or session errors
 * @returns {{ isReady: boolean, isLoading: boolean, isRecovering: boolean, error: object|null, fieldValidity: object, submit: function, sessionId: string|null }}
 */
export function useHostedPaymentFields({
  amount,
  currency = 'USD',
  description = '',
  clientConfig = null,
  onCardReady,
  onError
}) {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecovering, setIsRecovering] = useState(false);
  const [error, setError] = useState(null);
  const [fieldValidity, setFieldValidity] = useState(INITIAL_FIELD_VALIDITY);
  const [fieldsLoaded, setFieldsLoaded] = useState(INITIAL_FIELDS_LOADED);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs for stable lifecycle — prevent re-initialization on parent re-renders
  const hostedFieldsRef = useRef(null);
  const sessionRef = useRef(null);
  const initializingRef = useRef(false);
  const destroyedRef = useRef(false);
  const recoveryTimerRef = useRef(null);
  const recoveryCountRef = useRef(0);
  const MAX_RECOVERY_ATTEMPTS = 2;

  // Store callbacks in refs so messageHandler always sees latest without re-init
  const onCardReadyRef = useRef(onCardReady);
  const onErrorRef = useRef(onError);
  useEffect(() => { onCardReadyRef.current = onCardReady; }, [onCardReady]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Capture init params in refs (immutable after mount — EPG sessions can't change amount)
  const initParamsRef = useRef({ amount, currency, description, clientConfig });

  /**
   * Destroy the current HPF instance and reset all state for reinitialization.
   */
  const destroyInstance = useCallback(() => {
    if (hostedFieldsRef.current) {
      hostedFieldsRef.current.destroy?.();
      hostedFieldsRef.current = null;
    }
    sessionRef.current = null;
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
  }, []);

  /**
   * Initialize the full HPF lifecycle:
   * 1. Fetch client config (or use pre-fetched from CardEntry)
   * 2. Load HPF script + create payment session IN PARALLEL
   * 3. Construct ElavonHostedFields with messageHandler
   *
   * Called on mount and during auto-recovery after SDK errors.
   */
  const initRef = useRef(null);
  initRef.current = async (isRecovery = false) => {
    try {
      if (isRecovery) {
        setIsRecovering(true);
      }
      setIsLoading(true);
      setError(null);
      setFieldValidity(INITIAL_FIELD_VALIDITY);
      setFieldsLoaded(INITIAL_FIELDS_LOADED);
      setIsSubmitting(false);
      setIsReady(false);

      // 1. Get client config — use pre-fetched if available, otherwise fetch
      const config = initParamsRef.current.clientConfig
        || await paymentSessionService.getClientConfig();

      if (destroyedRef.current) return;

      // 2. Load HPF script + create payment session in parallel
      //    These are independent — script loads from CDN, session from our API
      const { amount: amt, currency: cur, description: desc } = initParamsRef.current;
      const [, session] = await Promise.all([
        loadHpfScript(config.hpfScriptUrl),
        paymentSessionService.createPaymentSession({
          amount: amt,
          currency: cur,
          description: desc
        })
      ]);
      sessionRef.current = session;

      if (destroyedRef.current) return;

      // Safety net — script loaded but global not available (CDN served wrong file, etc.)
      if (!window.ElavonHostedFields) {
        throw new Error('ElavonHostedFields not available after script load');
      }

      if (destroyedRef.current) return;

      // 3. Initialize ElavonHostedFields
      // eslint-disable-next-line no-undef
      // Surcharge is checked server-side via POST /surcharge-advices after vaulting
      // (per Elavon support, Justin — April 2026). However, the HPF SDK still detects
      // surcharge-eligible cards and pauses submission waiting for acknowledgement
      // before it will create the HostedCard. We auto-acknowledge so the SDK proceeds
      // immediately — surcharge calculation and consent are handled server-side.
      const hostedFields = new window.ElavonHostedFields({
        sessionId: session.sessionId,
        fields: FIELD_CONFIG,
        styles: HPF_STYLES,
        hideUntilRequested: false,
        onSurchargeAcknowledgementRequired: (_surchargeInfo, acknowledge) => {
          // Auto-acknowledge: let SDK create the HostedCard without pausing.
          // Surcharge is handled server-side via POST /surcharge-advices after vaulting.
          acknowledge();
        },
        onReady: () => {
          if (!destroyedRef.current) {
            setIsReady(true);
            setIsLoading(false);
            setIsRecovering(false);
            recoveryCountRef.current = 0; // Reset on successful init
          }
        },
        messageHandler: (message) => {
          if (destroyedRef.current) return;

          switch (message.type) {
            case 'fieldValidityChanged':
              setFieldValidity(prev => ({
                ...prev,
                [message.fieldType]: message.isValid
              }));
              break;

            case 'hostedCardCreated': {
              setIsSubmitting(false);
              // HPF returned a HostedCard (doCreateTransaction=false)
              // Data is under message.hostedCard (not message.transaction)
              // Surcharge is handled server-side after vaulting — not extracted from SDK
              onCardReadyRef.current?.({
                type: 'hostedCard',
                hostedCardId: message.hostedCard?.id,
                sessionId: session.sessionId,
                last4: message.hostedCard?.card?.last4,
                brand: message.hostedCard?.card?.brand,
                holderName: message.hostedCard?.card?.holderName,
                expirationMonth: message.hostedCard?.card?.expirationMonth,
                expirationYear: message.hostedCard?.card?.expirationYear
              });
              break;
            }

            case 'transactionCreated':
              setIsSubmitting(false);
              // HPF charged directly (doCreateTransaction=true)
              onCardReadyRef.current?.({
                type: 'transaction',
                transactionId: message.transaction?.id,
                isAuthorized: message.isAuthorized,
                state: message.transaction?.state,
                card: message.transaction?.card
              });
              break;

            case 'error':
              setIsSubmitting(false);
              setError(message);
              onErrorRef.current?.(message);

              // Auto-recover: destroy spent session and reinitialize.
              // HPF sessions are single-use — once an error occurs (e.g. "failed to
              // create hosted card"), the session is consumed and retrying will fail.
              // Brief delay lets the error message render before fields reset.
              // Cap retries to prevent infinite loops (e.g. persistent network failure).
              if (recoveryCountRef.current < MAX_RECOVERY_ATTEMPTS) {
                recoveryCountRef.current += 1;
                recoveryTimerRef.current = setTimeout(() => {
                  if (!destroyedRef.current) {
                    destroyInstance();
                    initializingRef.current = false;
                    initRef.current?.(true);
                  }
                }, 2000);
              }
              break;

            case 'fieldLoaded':
              setFieldsLoaded(prev => ({
                ...prev,
                [message.fieldType]: true
              }));
              break;

            // surchargeAdviceUpdated: handled server-side now, not in SDK
            // fieldFocusChanged, fieldValueChanged — ignored for now
            default:
              break;
          }
        }
      });

      hostedFieldsRef.current = hostedFields;

    } catch (err) {
      if (!destroyedRef.current) {
        const errorObj = {
          type: 'initError',
          message: err.response?.data?.error || err.message || 'Failed to initialize payment fields',
          original: err
        };
        setError(errorObj);
        setIsLoading(false);
        setIsRecovering(false);
        onErrorRef.current?.(errorObj);
      }
    }
  };

  /**
   * Mount effect — initialize once on mount, cleanup on unmount.
   */
  useEffect(() => {
    // Guard against double-init (React StrictMode calls useEffect twice in dev)
    if (initializingRef.current) return;
    initializingRef.current = true;
    destroyedRef.current = false;

    initRef.current?.();

    // Cleanup on unmount
    return () => {
      destroyedRef.current = true;
      destroyInstance();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Submit the HPF form.
   * Caller passes billing address data; HPF handles card data internally.
   * @param {object} [billTo] - Billing address for AVS
   * @param {string} [billTo.fullName] - Cardholder name (for billTo)
   * @param {string} [billTo.street1] - Street address
   * @param {string} [billTo.city] - City
   * @param {string} [billTo.region] - State/region code
   * @param {string} [billTo.postalCode] - ZIP/postal code
   * @param {string} [billTo.countryCode='USA'] - ISO 3166-1 Alpha-3 country code
   */
  const submit = useCallback((billTo = {}) => {
    if (!hostedFieldsRef.current) {
      const err = { type: 'submitError', message: 'Payment fields not initialized' };
      setError(err);
      onErrorRef.current?.(err);
      return;
    }

    const submitData = {};
    if (billTo && Object.keys(billTo).length > 0) {
      submitData.billTo = {
        countryCode: 'USA',
        ...billTo
      };
    }

    setIsSubmitting(true);
    hostedFieldsRef.current.submit(submitData);
  }, []);

  /**
   * Get current HPF field validation state.
   * @returns {{ isValid: boolean, fields: object }}
   */
  const getState = useCallback(() => {
    if (!hostedFieldsRef.current) {
      return { isValid: false, fields: fieldValidity };
    }
    return hostedFieldsRef.current.getState?.() || { isValid: false, fields: fieldValidity };
  }, [fieldValidity]);

  return {
    isReady,
    isLoading,
    isRecovering,
    isSubmitting,
    error,
    fieldValidity,
    fieldsLoaded,
    submit,
    getState,
    sessionId: sessionRef.current?.sessionId || null
  };
}
